import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { getApiUrl, setUnauthorizedHandler } from '../utils/api';

interface User {
  id: number;
  twitch_id: string;
  twitch_username: string;
  display_name?: string;
  profile_image_url?: string;
  role: 'super-admin' | 'admin' | 'editor';
  permissions: string[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  loginWithTwitch: () => void;
  logout: () => void;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  isEditor: boolean;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // When any apiFetch/api call gets a 401, treat the session as expired:
  // clear auth state and send the user back to the login page.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setToken(null);
      setUser(null);
      if (window.location.pathname.startsWith('/admin') &&
          window.location.pathname !== '/admin/login') {
        window.location.assign('/admin/login?error=session_expired');
      }
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  // Check for existing token on mount and handle OAuth callback
  useEffect(() => {
    // Check for OAuth callback token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const callbackToken = urlParams.get('token');
    const error = urlParams.get('error');
    const redirect = urlParams.get('redirect');

    if (error) {
      // OAuth error - will be handled by Login component
      setIsLoading(false);
      return;
    }

    if (callbackToken) {
      // Store the token from OAuth callback
      localStorage.setItem('auth_token', callbackToken);
      setToken(callbackToken);

      // Clean up URL (remove token and other params)
      const cleanPath = redirect || '/admin/dashboard';
      window.history.replaceState({}, '', cleanPath);

      // Fetch user info with the new token
      fetchCurrentUser(callbackToken);
      return;
    }

    // Check for existing token in localStorage
    const storedToken = localStorage.getItem('auth_token');
    if (storedToken) {
      fetchCurrentUser(storedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchCurrentUser = async (authToken: string) => {
    try {
      const response = await fetch(`${getApiUrl()}/admin_auth/me`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setToken(authToken);
      } else {
        // Token is invalid, clear it
        localStorage.removeItem('auth_token');
        setToken(null);
        setUser(null);
      }
    } catch (error) {
      console.error('Failed to fetch current user:', error);
      localStorage.removeItem('auth_token');
      setToken(null);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithTwitch = () => {
    // Get current path to redirect back after login
    const currentPath = window.location.pathname;
    const redirectPath = currentPath === '/admin/login' ? '/admin/dashboard' : currentPath;

    // Redirect to backend OAuth endpoint
    window.location.href = `${getApiUrl()}/admin_auth/login?redirect=${encodeURIComponent(redirectPath)}`;
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
  };

  const isSuperAdmin = user?.role === 'super-admin';
  const isAdmin = user?.role === 'admin' || user?.role === 'super-admin';
  const isEditor = user?.role === 'editor' || user?.role === 'admin' || user?.role === 'super-admin';

  // Check if user has a specific permission
  // Super-admins and admins have implicit access to all features
  const hasPermission = (permission: string): boolean => {
    if (isSuperAdmin || isAdmin) return true;
    return user?.permissions?.includes(permission) ?? false;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        loginWithTwitch,
        logout,
        isSuperAdmin,
        isAdmin,
        isEditor,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
